import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Link } from '@material-ui/core';

import i18n from '../../../i18n';
import cn from 'classnames';

import SellersFone from 'assets/images/sellersFone.png';
import Sellers_1_ru from 'assets/images/sellers_1_ru.png';
import Sellers_2_ru from 'assets/images/sellers_2_ru.png';
import Sellers_3_ru from 'assets/images/sellers_3_ru.png';
import Sellers_1_en from 'assets/images/sellers_1_en.png';
import Sellers_2_en from 'assets/images/sellers_2_en.png';
import Sellers_3_en from 'assets/images/sellers_3_en.png';
import { ReactComponent as YoutubeIcon } from 'assets/icons/main-24/youtube--24.svg';

import { Text, TextTypography } from 'components/Text/Text';
import { Header } from 'components/Header/Header';
import { FooterInternalPages } from 'components/Footer/FooterInternalPages';
import { Icon } from 'components/Icon/Icon';
import useScrollPosition from '@react-hook/window-scroll';
import { VendorSubscriptionRate } from '../Vendor/VendorSubscription/VendorSubscriptionRate';

import css from './Sellers.module.css';
import useVideoLink from '../../core/hooks/useVideoLink';

const VIDEO_URL = 'https://www.youtube.com/embed/aIHdFlPiIYQ?controls=1&autoplay=1&mute=1';
const EN_VIDEO_URL = 'https://www.youtube.com/embed/gnbpAT1x7AE?controls=1&autoplay=1&mute=1';

const useStyles = makeStyles(theme => ({
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        whiteSpace: 'pre-line',
    },
    headerContainer: {
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        zIndex: 1,
        backgroundColor: (props: { scrollY: number }) => (props.scrollY > 664 ? '#FBFEF9' : null),
    },
    tagline: {
        height: 664,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: `url(${SellersFone}) no-repeat`,
        backgroundSize: 'cover',
        backgroundPosition: '60%',
    },
    taglineLink: {
        display: 'flex',
        alignContent: 'center',
        marginTop: theme.spacing(5),
        marginLeft: theme.spacing(17.75),
        height: 24,
        [theme.breakpoints.down(1280)]: {
            marginLeft: theme.spacing(5),
        },
        [theme.breakpoints.down(768)]: {
            marginLeft: theme.spacing(4),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: theme.spacing(2),
        },
    },
    taglineLinkText: {
        marginLeft: theme.spacing(1),
        color: '#FFFFFF',
        fontSize: 16,
        lineHeight: 1.5,
        [theme.breakpoints.down(321)]: {
            fontSize: 14,
        },
    },
    taglineText: {
        maxWidth: 537,
        marginLeft: theme.spacing(17.75),
        color: '#FFFFFF',
        textShadow: '0px 0px 60px rgba(0, 0, 0, 0.8)',
        [theme.breakpoints.down(1280)]: {
            marginLeft: theme.spacing(5),
        },
        [theme.breakpoints.down(768)]: {
            marginLeft: theme.spacing(4),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: theme.spacing(2),
        },
    },
    additionalContainer: {
        width: '100%',
        padding: theme.spacing(10, 17.75),
        margin: '0 auto',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.down(1280)]: {
            padding: theme.spacing(10, 5),
        },
        [theme.breakpoints.down(960)]: {
            padding: theme.spacing(8, 5),
        },
        [theme.breakpoints.down(768)]: {
            padding: theme.spacing(6, 4),
        },
        [theme.breakpoints.down(480)]: {
            padding: theme.spacing(6, 2),
        },
    },
    additionalTitle: {
        width: '100%',
        marginTop: theme.spacing(10),
        '&:first-child': {
            marginTop: 0,
        },
        [theme.breakpoints.down(960)]: {
            marginTop: theme.spacing(8),
        },
        [theme.breakpoints.down(768)]: {
            marginTop: theme.spacing(6),
        },
    },
    additionalTitleText: {
        letterSpacing: -1,
        [theme.breakpoints.down(321)]: {
            fontSize: 24,
            lineHeight: 1.33,
        },
    },
    additionalSubTitle: {
        width: 'inherit',
        textAlign: 'left',
        marginTop: theme.spacing(3),
        '&:last-child': {
            marginTop: theme.spacing(4),
        },
    },
    gridBig: {
        width: '100%',
        margin: theme.spacing(8, 0),
        overflow: 'hidden',
        border: '1px solid rgba(36, 49, 67, 0.2)',
        boxShadow: '0px 15px 32px -3px rgba(36, 49, 67, 0.2)',
        boxSizing: 'border-box',
        borderRadius: theme.spacing(1),
        [theme.breakpoints.down(960)]: {
            margin: theme.spacing(6, 0),
        },
        [theme.breakpoints.down(768)]: {
            margin: theme.spacing(5, 0),
        },
        [theme.breakpoints.down(480)]: {
            margin: theme.spacing(4, 0),
        },
    },
    additionalImg: {
        objectFit: 'fill',
        width: '100%',
        height: '100%',
        borderRadius: theme.spacing(1),
    },
    footerContainer: {
        width: '100%',
        margin: '0 auto',
    },
    link: {
        maxHeight: 24,
        display: 'flex',
        alignItem: 'center',
        textDecoration: 'none',
    },
    videoContainer: {
        position: 'relative',
        width: '100%',
        height: 0,
        paddingBottom: '56.25%',
    },
    video: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
}));

export const Sellers = () => {
    const scrollY = useScrollPosition(60 /*fps*/);
    const classes = useStyles({ scrollY });
    const {
        t,
        i18n: { language },
    } = i18n.useTranslation();
    const isRussianLanguage = language === 'ru';
    const Sellers_1 = isRussianLanguage ? Sellers_1_ru : Sellers_1_en;
    const Sellers_2 = isRussianLanguage ? Sellers_2_ru : Sellers_2_en;
    const Sellers_3 = isRussianLanguage ? Sellers_3_ru : Sellers_3_en;
    const videoUrlEmbed = useMemo(() => (isRussianLanguage ? VIDEO_URL : EN_VIDEO_URL), [isRussianLanguage]);
    const videoLink = useVideoLink();

    return (
        <div className={classes.mainContainer}>
            <div className={classes.headerContainer}>
                <Header />
            </div>

            <div className={classes.tagline}>
                <Text className={classes.taglineText} typography={TextTypography.TITLE_STRONG_CHANGE}>
                    {t('pages:sellers.title')}
                </Text>
                <div className={classes.taglineLink}>
                    <Icon component={YoutubeIcon} />
                    <Link className={classes.link} href={videoLink} target="_blank" rel="noreferrer" color="inherit">
                        <Text className={classes.taglineLinkText} typography={TextTypography.TEXT_ACCENT_MEDIUM_CHANGE}>
                            {t('pages:sellers.subtitle')}
                        </Text>
                    </Link>
                </div>
            </div>
            <div className={classes.additionalContainer}>
                <div className={classes.additionalTitle}>
                    <Text className={classes.additionalTitleText} typography={TextTypography.HUGE_TITLE_BOLD_CHANGE}>
                        {t('pages:sellers.block1.title')}
                    </Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text className={classes.additionalTitleText} typography={TextTypography.TITLE_ACCENT_CHANGE}>
                        {t('pages:sellers.block1.subtitle')}
                    </Text>
                </div>
                <div className={classes.videoContainer}>
                    <iframe
                        className={classes.video}
                        src={videoUrlEmbed}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text1')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text2')}</Text>
                </div>

                {isRussianLanguage ? (
                    <div className={css.advantages}>
                        <a href="http://info.any.market/page-ready-design.html" className={css.advantagesItem}>
                            <div className="box">
                                <img
                                    src="http://info.any.market/assets/image/section-advantages/website--16.svg"
                                    alt=""
                                />
                                <h5>{t('pages:sellers.designReady')}</h5>
                                <p>{t('pages:sellers.more')}</p>
                            </div>
                        </a>

                        <a href="http://info.any.market/page-delivery.html" className={css.advantagesItem}>
                            <div className="box">
                                <img src="http://info.any.market/assets/image/section-advantages/fast--16.svg" alt="" />
                                <h5>{t('pages:sellers.deliveryReady')}</h5>
                                <p>{t('pages:sellers.more')}</p>
                            </div>
                        </a>

                        <a href="http://info.any.market/page-payment.html" className={css.advantagesItem}>
                            <div className="box">
                                <img src="http://info.any.market/assets/image/section-advantages/card--16.svg" alt="" />
                                <h5>{t('pages:sellers.paymentReady')}</h5>
                                <p>{t('pages:sellers.more')}</p>
                            </div>
                        </a>

                        <a href="http://info.any.market/page-map-location.html" className={css.advantagesItem}>
                            <div className="box">
                                <img
                                    src="http://info.any.market/assets/image/section-advantages/map-marker--16.svg"
                                    alt=""
                                />
                                <h5>{t('pages:sellers.mapReady')}</h5>
                                <p>{t('pages:sellers.more')}</p>
                            </div>
                        </a>

                        <a href="http://info.any.market/page-qr.html" className={css.advantagesItem}>
                            <div className="box">
                                <img
                                    src="http://info.any.market/assets/image/section-advantages/image-o--16.svg"
                                    alt=""
                                />
                                <h5>{t('pages:sellers.qrReady')}</h5>
                                <p>{t('pages:sellers.more')}</p>
                            </div>
                        </a>
                    </div>
                ) : (
                    <div className={css.advantages}>
                        <div className={cn(css.advantagesItem, css.advantagesItemEn)}>
                            <div className="box">
                                <img
                                    src="http://info.any.market/assets/image/section-advantages/website--16.svg"
                                    alt=""
                                />
                                <h5>{t('pages:sellers.designReady')}</h5>
                            </div>
                        </div>

                        <div className={cn(css.advantagesItem, css.advantagesItemEn)}>
                            <div className="box">
                                <img src="http://info.any.market/assets/image/section-advantages/fast--16.svg" alt="" />
                                <h5>{t('pages:sellers.deliveryReady')}</h5>
                            </div>
                        </div>

                        <div className={cn(css.advantagesItem, css.advantagesItemEn)}>
                            <div className="box">
                                <img src="http://info.any.market/assets/image/section-advantages/card--16.svg" alt="" />
                                <h5>{t('pages:sellers.paymentReady')}</h5>
                            </div>
                        </div>

                        <div className={cn(css.advantagesItem, css.advantagesItemEn)}>
                            <div className="box">
                                <img
                                    src="http://info.any.market/assets/image/section-advantages/map-marker--16.svg"
                                    alt=""
                                />
                                <h5>{t('pages:sellers.mapReady')}</h5>
                            </div>
                        </div>

                        <div className={cn(css.advantagesItem, css.advantagesItemEn)}>
                            <div className="box">
                                <img
                                    src="http://info.any.market/assets/image/section-advantages/image-o--16.svg"
                                    alt=""
                                />
                                <h5>{t('pages:sellers.qrReady')}</h5>
                            </div>
                        </div>
                    </div>
                )}

                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text3')}</Text>
                </div>
                <Grid container>
                    <Grid item className={classes.gridBig}>
                        <img className={classes.additionalImg} src={Sellers_1} alt={t('common:photoAltShop')} />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text4')}</Text>
                </div>
                <Grid container>
                    <Grid item className={classes.gridBig}>
                        <img className={classes.additionalImg} src={Sellers_2} alt={t('common:photoAltShop')} />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text5')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text6')}</Text>
                </div>
                <Grid container>
                    <Grid item className={classes.gridBig}>
                        <img className={classes.additionalImg} src={Sellers_3} alt={t('common:photoAltShop')} />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text7')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text8')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:sellers.block1.text9')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text className={classes.additionalTitleText} typography={TextTypography.TITLE_ACCENT_CHANGE}>
                        {t('pages:sellers.block2.title')}
                    </Text>
                </div>
                <div>
                    <VendorSubscriptionRate isVendorPage={false} />
                </div>
            </div>
            <div className={classes.footerContainer}>
                <FooterInternalPages />
            </div>
        </div>
    );
};
